import SectionCard, {
  SectionCardControls,
  SectionCardGroup
} from "../../../../Composites/SectionCard/SectionCard";
import { List, Button, Flex, EditableText } from "@theorgchartdev/storybook";
import { GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import React, { useState, useEffect } from "react";
import ActionListItem from "../../../../Composites/ActionListItem/ActionListItem";
import { DoFileOperationAsync, DownloadMetadata, LoadDirectory } from "@/Utils/Promises";
import { QDataRefreshRFC } from "@coreapp/CoreApp/QDataRefreshRFC";
import { QWaitingPanel } from "@coreapp/CoreApp/QWaitingPanel";
import { appstr } from "@coreapp/utils/appstr";
import { SetOnBeforeUnload } from "@coreapp/utils/SetOnBeforeUnload";
import { Globals } from "@coreapp/CoreApp/Globals";
import { FileDropZone } from "@/appComponents/Blocks/Composites/FileDropZone/FileDropZone";
import { useToast } from "@/providers/ToastProvider";
import { useTopic } from "@/hooks/useTopic";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";

const ext = ".oc5c";

const BuildCheckpointList = (checkpoints, defaultCheckpoint, handleUpdate) => {
  let checkpointOptions = [];
  if (!checkpoints) return checkpointOptions;
  for (let checkpoint of checkpoints) {
    let isDefault = checkpoint === defaultCheckpoint;
    const checkpointContent = {
      content: (
        <ActionListItem
          padding="large"
          contentBefore={
            <EditableText
              className="oc-fill-width"
              size={"small"}
              bold={true}
              text={checkpoint ? checkpoint : appstr("Untitled")}
              onChange={newName => handleUpdate("rename", checkpoint, { newName: newName })}
              dataTestId="account-settings-checkpoints-row-rename-input"
            />
          }
          actions={[
            {
              iconName: isDefault ? "star-solid" : "star-regular",
              onClick: () => handleUpdate("default", checkpoint),
              checkActive: () => isDefault,
              iconProps: {
                color: "brand"
              },
              dataTestId: "account-settings-checkpoints-row-set-default-btn"
            },
            {
              iconName: "file-export",
              onClick: () => handleUpdate("download", checkpoint),
              dataTestId: "account-settings-checkpoints-row-download-btn"
            },
            {
              iconName: "trash-can",
              iconProps: { color: "error" },
              onClick: () => handleUpdate("delete", checkpoint),
              dataTestId: "account-settings-checkpoints-row-delete-btn"
            }
          ]}
        />
      ),
      padding: false,
      divider: true,
      value: checkpoint
    };
    checkpointOptions.push(checkpointContent);
  }
  return checkpointOptions;
};

export const Checkpoints = ({ accountConfig, handleUpdate }) => {
  const [checkpoints, setCheckpoints] = useState(null);
  const wsMgr = GetWSMgr();

  const { addToast } = useToast();

  useTopic("FileUploadComplete", async ({ success }) => {
    if (!success) return;
    await LoadCheckpoints();
  });

  const LoadCheckpoints = async () => {
    const { success, processedResults } = await LoadDirectory("System", "checkpoints");
    if (success) setCheckpoints(processedResults);
  };

  useEffect(() => {
    const eventTracker = wsMgr.GetUsageTracker();
    eventTracker.ReportEvent("account_settings.checkpoints.opened");

    LoadCheckpoints();
  }, []);

  const CreateNewCheckpoint = async () => {
    const { filename } = await DownloadMetadata();
    if (filename) {
      PostCheckpointCreation(filename);
      addToast({
        ...createBoldToastMessage("Entity created", { tableName: filename.split(".oc5c")[0] }),
        status: "success",
        timeoutMS: 5000
      });
    } else {
      addToast({ message: appstr("An error occurred while saving"), status: "error" });
    }
  };

  const PostCheckpointCreation = filename => {
    const u = wsMgr.GetUploadMgr();
    const accountName = wsMgr.GetAccountName();
    const username = wsMgr.GetUserName();
    const directory = "System";
    LoadCheckpoints();
    u.DoDownloadFile(accountName, username, directory, filename);
    SetOnBeforeUnload();
    ReRenderCanvas();
  };

  const RevertToCheckpoint = () => {
    wsMgr.CreateReactAlert(
      appstr("Alert"),
      appstr("dataresetconfirm"),
      ConfirmRevertCheckpoint,
      null,
      appstr("Reset Charts"),
      "Cancel"
    );
  };

  const ConfirmRevertCheckpoint = () => {
    const wsMgr = GetWSMgr();
    const accountName = wsMgr.GetAccountName();
    const username = wsMgr.GetUserName();
    var resetDataRFC = new QDataRefreshRFC();
    resetDataRFC.InitQDataRefreshQuery(this);
    resetDataRFC.SetParamsNoChart(accountName, username, "reset", "");
    Globals._rfcMgr.QueueRFC(resetDataRFC);
    wsMgr.fOrgInfo.SetMasterChartList(null);
    var waiting = new QWaitingPanel();
    waiting.InitWaitingPanel(
      appstr("dataresetinprogress") + "...",
      wsMgr.fBackgroundPanel,
      wsMgr,
      220
    );
    wsMgr.fWaiting = waiting;
  };

  const handleOption = (type, checkpoint, data = {}) => {
    switch (type) {
      case "default":
        handleUpdate({ checkpoint: checkpoint }, "checkpoint");
        addToast({
          ...createBoldToastMessage("Entity set as default", { entity: checkpoint }),
          status: "success",
          timeoutMS: 5000
        });
        break;
      case "delete":
        wsMgr.CreateReactAlert(
          appstr("permanentaction"),
          appstr("checkdelconfirm") + " '" + checkpoint + "'",
          async () => {
            await handleDelete(checkpoint);
          },
          null,
          appstr("Delete"),
          appstr("Cancel")
        );
        break;
      case "rename":
        handleRename(checkpoint, data.newName);
        break;
      case "download":
        if (checkpoint.indexOf(ext) < 0) checkpoint += ext;
        wsMgr.DownloadFile("System", checkpoint);
        break;
    }
  };

  const UploadCheckpoint = () => {
    const w = GetWSMgr();
    const uploadCmd = { directory: "System", fileTypes: ["oc5c"], skipFM: true };
    w.UploadFile(uploadCmd);
  };

  const handleRename = async (curName, newName) => {
    try {
      const oldName = curName.indexOf(ext) < 0 ? curName + ext : curName;
      const targetName = newName.indexOf(ext) < 0 ? newName + ext : newName;

      const accountName = wsMgr.GetAccountName();
      const username = wsMgr.GetUserName();

      const result = await DoFileOperationAsync("rename", {
        curname: oldName,
        newname: targetName,
        entitytype: "file",
        dir: "System",
        accountName: accountName,
        username: username
      });

      LoadCheckpoints();
      addToast({
        ...createBoldToastMessage("Entity renamed", {
          oldEntity: curName,
          newEntity: newName
        }),
        status: "success",
        timeoutMS: 5000
      });
      return true;
    } catch (error) {
      addToast({ message: appstr("An error occurred while saving"), status: "error" });
      return false;
    }
  };

  const handleDelete = async checkpoint => {
    try {
      const deleteName = checkpoint.indexOf(ext) < 0 ? checkpoint + ext : checkpoint;
      const wsMgr = GetWSMgr();
      const accountName = wsMgr.GetAccountName();
      const username = wsMgr.GetUserName();

      const result = await DoFileOperationAsync("delete", {
        names: deleteName,
        entitytype: "file",
        thePath: "System",
        accountName: accountName,
        username: username
      });
      addToast({
        ...createBoldToastMessage("Entity deleted", {
          entity: checkpoint
        }),
        status: "success",
        timeoutMS: 5000
      });

      LoadCheckpoints();
      return true;
    } catch (error) {
      addToast({ message: appstr("An error occurred while saving"), status: "error" });
      return false;
    }
  };

  return (
    <FileDropZone
      targetDirectory={"System"}
      allowMultiUpload={false}
      extensionRestrictions={["oc5c"]}
      id={`checkpoint-drop-zone}`}
      data-testid="account-settings-checkpoints-drop-zone"
    >
      <SectionCardGroup>
        <SectionCardControls>
          <Button
            modifier={"secondary"}
            className={"oc-special-secondary"}
            iconProps={{ name: "plus" }}
            onClick={CreateNewCheckpoint}
            dataTestId="account-settings-checkpoints-create-btn"
          >
            {appstr("Create checkpoint")}
          </Button>
          <Button
            modifier={"secondary"}
            onClick={UploadCheckpoint}
            dataTestId="account-settings-checkpoints-upload-btn"
          >
            {appstr("Upload checkpoint")}
          </Button>
          <Flex className={"oc-right-justify"}>
            <Button
              modifier={"secondary"}
              iconProps={{ name: "stopwatch" }}
              onClick={RevertToCheckpoint}
              dataTestId="account-settings-checkpoints-revert-btn"
            >
              {appstr("Revert to checkpoint")}
            </Button>
          </Flex>
        </SectionCardControls>
        <SectionCard title={appstr("Checkpoints")}>
          <List
            enableVirtualization={false}
            options={BuildCheckpointList(checkpoints, accountConfig?.checkpoint, handleOption)}
            listWidth="fill"
            optionSize={"large"}
            showCheck={false}
          />
        </SectionCard>
      </SectionCardGroup>
    </FileDropZone>
  );
};
